import React, { useEffect, useState } from "react";

import { useTranslation } from "utils";

import { Button, Flex, FormFlowNavBar, FullScreenOverlay, PageStatus, StickyFooterButtons } from "components";

export interface FormPage {
  title: string;
  category?: string;
  body: () => JSX.Element;
}

export type FormFlowOverlayProps = {
  onFormClose?: () => void;
  showModal: boolean;
  formPages: FormPage[];
  getPageStatus?: (index: number) => PageStatus | null;
  navBarTitle?: string;
  dataTestId?: string;
  initialStep?: number;
  currentStep?: number;
  onStepChange?: (step: number) => void;
  editMode?: boolean;
  completionPercent?: number;
};

export const FormFlowOverlay = ({
  onFormClose,
  showModal,
  formPages,
  getPageStatus,
  navBarTitle,
  dataTestId,
  initialStep,
  currentStep,
  onStepChange,
  editMode,
  completionPercent,
}: FormFlowOverlayProps) => {
  const { t } = useTranslation("nav");
  const [internalPageIndex, setInternalPageIndex] = useState(initialStep ?? 0);
  const activePageIndex = currentStep !== undefined ? currentStep : internalPageIndex;

  const setActivePageIndex = (index: number) => {
    if (currentStep !== undefined) {
      onStepChange?.(index);
    } else {
      setInternalPageIndex(index);
    }
  };

  const resetModalScroll = () => {
    const modalBody = document.querySelector(".ant-modal-body");
    if (modalBody) modalBody.scrollTop = 0;
  };

  const handleChangeActivePageIndex = (diff: number) => {
    resetModalScroll();
    setActivePageIndex(activePageIndex + diff);
  };

  const handleClose = () => {
    setActivePageIndex(0);
    onFormClose?.();
  };

  useEffect(() => {
    if (currentStep === undefined && Number.isInteger(initialStep)) {
      setInternalPageIndex(initialStep ?? 0);
    }
  }, [initialStep, currentStep]);

  return (
    <FullScreenOverlay
      dataTestId={dataTestId}
      onClose={handleClose}
      showModal={showModal}
      closeIconTooltip={t("overlay.saveAndClose")}
      wrapClassName="full-screen-overlay overlay-animation"
    >
      <FormFlowNavBar
        title={navBarTitle}
        activePageNumber={activePageIndex}
        handleSetActivePageNumber={setActivePageIndex}
        pages={formPages.map((step) => ({ title: step.title, category: step.category }))}
        getPageStatus={(i) => (getPageStatus ? getPageStatus(i + 1) : null)}
      />
      <section key={`step-${activePageIndex}`} className="full-screen-overlay-content">
        <section className="container tiniest margin-top-400">{formPages[activePageIndex].body()}</section>
        <StickyFooterButtons absolute progressBarPercent={completionPercent}>
          <Flex justify="space-between">
            {editMode && (
              <Button className="footer-button" dataTestId="overlay-next-button" type="primary" onClick={handleClose}>
                {t("overlay.saveAndClose")}
              </Button>
            )}
            {!editMode &&
              (activePageIndex > 0 ? (
                <>
                  <Button dataTestId="overlay-back-button" type="tertiary" onClick={() => handleChangeActivePageIndex(-1)} className="footer-button">
                    {t("overlay.back")}
                  </Button>
                  <Button
                    className="footer-button"
                    dataTestId="overlay-next-button"
                    type="primary"
                    onClick={activePageIndex + 1 === formPages.length ? handleClose : () => handleChangeActivePageIndex(1)}
                  >
                    {activePageIndex + 1 === formPages.length ? t("overlay.saveAndClose") : t("overlay.saveAndContinue")}
                  </Button>
                </>
              ) : (
                <Button dataTestId="overlay-start-button" block type="primary" onClick={() => handleChangeActivePageIndex(1)} className="footer-button">
                  {t("overlay.saveAndContinue")}
                </Button>
              ))}
          </Flex>
        </StickyFooterButtons>
      </section>
    </FullScreenOverlay>
  );
};
